import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Flex, Text, Heading, Card, Switch, Button, Callout } from "@radix-ui/themes";
import { useProfileContext } from "context";
import { useAIAssistantSettings } from "hooks/aiAssistant/useAIAssistantSettings";
import { useWebsiteSettings, useUpdateWebsiteSettings, useRetryWebsiteSync } from "hooks/aiAssistant/useWebsiteSettings";
import { AssistantStatus, AssistantLoadError } from "./AssistantStatus";
import { normalizeWebsiteOrigin } from "./websiteOrigins";
import "./AIAssistantWebsite.css";

export default function AIAssistantWebsite() {
  const { userProfile } = useProfileContext();
  return userProfile?.user_role === "Owner" ? <WebsiteSettings /> : <Text role="alert">Only organisation Owners can access AI Assistant settings.</Text>;
}

function WebsiteSettings() {
  const query = useWebsiteSettings();
  const assistant = useAIAssistantSettings();
  const update = useUpdateWebsiteSettings();
  const retry = useRetryWebsiteSync();
  const [draft, setDraft] = useState(null);
  const [origin, setOrigin] = useState("");
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const inFlight = useRef(false);
  // Refetches must not silently discard an Owner's unsaved edits.
  useEffect(() => {
    if (query.data && !dirty) setDraft({
      enabled: query.data.enabled, allowed_origins: query.data.allowed_origins,
      session_ttl_seconds: query.data.session_ttl_seconds,
      max_messages_per_session: query.data.max_messages_per_session,
    });
  }, [query.data, dirty]);
  const change = (key, value) => { setDirty(true); setDraft(previous => ({ ...previous, [key]: value })); };
  const addOrigin = () => {
    try {
      const normalized = normalizeWebsiteOrigin(origin);
      if (!draft.allowed_origins.includes(normalized)) {
        if (draft.allowed_origins.length >= 100) throw new Error("At most 100 website origins are allowed.");
        change("allowed_origins", [...draft.allowed_origins, normalized].sort());
      }
      setOrigin(""); setError("");
    } catch (failure) { setError(failure.message); }
  };
  const synchronize = async (isRetry = false) => {
    if (inFlight.current || update.isPending || retry.isPending) return;
    if (!isRetry && origin) { setError("Add or clear the website origin before saving."); return; }
    if (!isRetry && (!Number.isInteger(draft.session_ttl_seconds) || draft.session_ttl_seconds < 60 || draft.session_ttl_seconds > 86400
      || !Number.isInteger(draft.max_messages_per_session) || draft.max_messages_per_session < 1 || draft.max_messages_per_session > 1000)) {
      setError("Use a session lifetime from 1 to 1,440 minutes and a message limit from 1 to 1,000."); return;
    }
    inFlight.current = true; setError("");
    try {
      await (isRetry ? retry.mutateAsync() : update.mutateAsync(draft));
      if (!isRetry) setDirty(false);
    } catch (failure) {
      setError(failure.response?.data?.detail || "Website synchronization could not be confirmed. Reload and retry.");
    } finally { inFlight.current = false; }
  };
  if (query.isPending || assistant.isPending || !draft) {
    if (query.isError || assistant.isError) return <AssistantLoadError error={query.error || assistant.error} onRetry={() => { query.refetch(); assistant.refetch(); }} />;
    return <Text role="status">Loading website settings…</Text>;
  }
  if (query.isError || assistant.isError) return <AssistantLoadError error={query.error || assistant.error} onRetry={() => { query.refetch(); assistant.refetch(); }} />;
  const settings = query.data;
  const central = assistant.data;
  const available = central.configured && central.public_assistant_id && central.enabled && central.runtime_enabled === true && central.sync_status === "synced";
  const busy = update.isPending || retry.isPending;
  const unresolved = ["pending", "error"].includes(settings.sync_status);
  const minutes = draft.session_ttl_seconds / 60;
  const lifetime = draft.session_ttl_seconds % 3600 === 0 ? `${draft.session_ttl_seconds / 3600} hours` : `${minutes} minutes`;
  return <Flex direction="column" gap="5" width="100%" maxWidth="720px">
    <Heading size="6">Website</Heading>
    {!available && <Callout.Root color="amber"><Callout.Text>
      Enable the AI Assistant in Settings before activating the website channel. Website configuration is retained while the central Assistant is disabled.
      {" "}<Link to="/ai-assistant/settings">Open Settings</Link>
    </Callout.Text></Callout.Root>}
    <Card><form onSubmit={event => { event.preventDefault(); synchronize(); }}>
      <Flex direction="column" gap="5">
        <Flex justify="between" align="center" gap="4">
          <Flex direction="column" gap="1"><Text as="label" htmlFor="website-enabled" weight="medium">Website Assistant</Text>
            <Text size="2" color="gray">Allow your AI Assistant to be embedded on approved websites.</Text>
            <Text size="2">{draft.enabled ? "Enabled" : "Disabled"}{dirty ? " (unsaved)" : ""}</Text>
          </Flex>
          <Switch id="website-enabled" aria-label="Website Assistant" checked={draft.enabled} disabled={busy || (!available && !draft.enabled)} onCheckedChange={value => change("enabled", value)} />
        </Flex>
        <AssistantStatus settings={settings} />
        {!available && <Text size="2">Website use is unavailable while the central Assistant is disabled or unconfirmed.</Text>}
        {settings.sync_status === "unconfigured" && <Text size="2">Website settings have never been synchronized.</Text>}
        {(busy || settings.sync_status === "pending") && <Text role="status">Saving website configuration… Runtime confirmation is pending.</Text>}
        {(error || settings.last_sync_error) && <Callout.Root color="red" role="alert"><Callout.Text>{error || settings.last_sync_error}</Callout.Text></Callout.Root>}
        <fieldset disabled={busy} className="ai-website-fields">
          <legend>Allowed websites</legend>
          <Text size="2" color="gray">Enter the website origin only — do not include a page path.</Text>
          <Text size="2" color="gray">Example: https://lacity.phpdev.ehost.mu. Exact origins only; no wildcard subdomains.</Text>
          <div className="ai-website-origin-input"><input aria-label="Website origin" placeholder="https://www.example.com" value={origin} onChange={event => setOrigin(event.target.value)} onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); addOrigin(); } }} />
            <Button type="button" variant="soft" onClick={addOrigin} disabled={busy || !origin}>Add website</Button></div>
          <ul>{draft.allowed_origins.map(value => <li key={value}><span>{value}</span><Button type="button" variant="soft" color="gray" aria-label={`Remove ${value}`} onClick={() => change("allowed_origins", draft.allowed_origins.filter(item => item !== value))}>Remove</Button></li>)}</ul>
          {!draft.allowed_origins.length && <Text size="2" color="gray">No approved websites yet.</Text>}
          <label htmlFor="website-ttl">Session lifetime (minutes)</label>
          <input id="website-ttl" aria-label="Session lifetime (minutes)" type="number" min="1" max="1440" step="any" value={Number.isFinite(minutes) ? minutes : ""} onChange={event => change("session_ttl_seconds", event.target.value === "" ? NaN : Math.round(Number(event.target.value) * 60))} />
          <Text size="2" color="gray">{Number.isFinite(minutes) ? lifetime : "Choose a session lifetime"} · 1 minute to 24 hours</Text>
          <label htmlFor="website-message-cap">Messages per session</label>
          <input id="website-message-cap" aria-label="Messages per session" type="number" min="1" max="1000" step="1" value={Number.isNaN(draft.max_messages_per_session) ? "" : draft.max_messages_per_session} onChange={event => change("max_messages_per_session", event.target.value === "" ? NaN : Number(event.target.value))} />
        </fieldset>
        <Flex gap="3" wrap="wrap">
          <Button type="submit" disabled={busy || (draft.enabled && !available)}>{busy ? "Saving…" : "Save website settings"}</Button>
          {unresolved && <Button type="button" variant="soft" disabled={busy || (settings.enabled && !available)} onClick={() => synchronize(true)}>Retry synchronization</Button>}
          <Button type="button" variant="soft" color="gray" disabled={busy} onClick={() => { setDirty(false); query.refetch(); assistant.refetch(); }}>Reload settings</Button>
        </Flex>
        {dirty && unresolved && <Text size="2">Retry synchronizes the last saved configuration. Save to apply your edits.</Text>}
      </Flex>
    </form></Card>
  </Flex>;
}
